import {useQuery} from '@tanstack/react-query'
import type {SuggestLocation} from '@yandex-delivery/api/model/suggest'
import type {Point} from '@yandex-delivery/core/types'
import {useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {type AddressPin, useFetchAddressPin} from '../../../../../api/address'
import {useConfigContext} from '../../../../../contexts/ConfigContext'

const getGeolocationFromBrowser = (): Promise<SuggestLocation> =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({coords}) => {
        resolve([coords.longitude, coords.latitude])
      },
      reject,
      {
        enableHighAccuracy: true,
        maximumAge: 60000,
        timeout: 2000,
      },
    )
  })

/**
 * Предзаполняет адрес отправления текущей локацией пользователя.
 * @param fieldName Название поля в стейте
 * @param isSource Является ли адрес - точкой отправления ("Откуда")
 * @param pointId Идентификатор точки
 * @param pointLocation координаты точки
 */
export const usePrefetchAddress = (fieldName: string, isSource: boolean, pointId: string, pointLocation?: Point) => {
  const {location: configLocation, initialProps} = useConfigContext()
  const {getValues} = useFormContext()
  const {field} = useController({name: fieldName})

  const updateState = useCallback(
    (addressPin: AddressPin) => {
      field.onChange(addressPin)
    },
    [field],
  )

  const {mutate: mutateAddressPin} = useFetchAddressPin({onSuccess: updateState})

  const {isLoading} = useQuery({
    queryKey: [`get-user-location-${pointLocation}`],
    queryFn: async () => {
      const value = getValues(fieldName)
      const isDestinationFromQuery = initialProps?.to && pointId === 'destination_1'

      if ((!isDestinationFromQuery && !isSource) || value?.title) return Promise.resolve(() => {})

      try {
        const location = await getGeolocationFromBrowser()
        mutateAddressPin({coordinates: pointLocation || location})
      } catch {
        mutateAddressPin({coordinates: pointLocation || configLocation})
      }
    },
  })

  return {isLoading} as const
}
